import { useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useAuthState } from '@/auth/authState';
import { useGetProductExperienceQuery } from '@/features/user/api/userApi';
import { initializePendo } from '@/services/pendoService';

/**
 * Initialises Pendo analytics once the backend has told us who this session is.
 *
 * The context — subscription, visitor, account — comes from `/me/product-experience` rather than
 * being derived here, so the agent reports the same identity as the events the backend sends
 * itself (see `pendoService`). Gated on auth being ready for the same reason `useIdentity` is:
 * this hook runs on every route, including `/auth/callback`, and an ungated request would fire
 * before the code→token exchange completes (COPA-221).
 *
 * A failed request means no analytics, never a broken page: RTK Query keeps the error, `data`
 * stays undefined, and the effect simply does not run.
 */
export function usePendo(): void {
  const { isAuthenticated, isLoading } = useAuthState();
  const ready = isAuthenticated && !isLoading;
  const { data: context } = useGetProductExperienceQuery(ready ? undefined : skipToken);

  useEffect(() => {
    if (context) {
      initializePendo(context);
    }
  }, [context]);
}
